import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Loader2, Pencil, X, Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface PromoCode {
  id: string;
  code: string;
  discount_percentage: number;
  is_active: boolean;
  max_uses: number | null;
  times_used: number;
  expires_at: string | null;
  created_at: string;
}

const AdminPromoCodes = () => {
  const [promos, setPromos] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState({ code: "", discount_percentage: 0, max_uses: "", expires_at: "" });
  const [newPromo, setNewPromo] = useState({ code: "", discount_percentage: 0, max_uses: "", expires_at: "" });
  const [showAdd, setShowAdd] = useState(false);
  const { toast } = useToast();

  useEffect(() => { fetchPromos(); }, []);

  const fetchPromos = async () => {
    try {
      const { data, error } = await supabase
        .from("promo_codes")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setPromos(data || []);
    } catch (error) {
      console.error("Error fetching promo codes:", error);
      toast({ title: "Error", description: "Failed to load promo codes", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const addPromo = async () => {
    if (!newPromo.code || !newPromo.discount_percentage) {
      toast({ title: "Error", description: "Code and discount percentage are required", variant: "destructive" });
      return;
    }
    try {
      const { error } = await supabase.from("promo_codes").insert({
        code: newPromo.code.toUpperCase().trim(),
        discount_percentage: newPromo.discount_percentage,
        max_uses: newPromo.max_uses ? parseInt(newPromo.max_uses) : null,
        expires_at: newPromo.expires_at || null,
      } as any);
      if (error) throw error;
      toast({ title: "Success", description: "Promo code created" });
      setNewPromo({ code: "", discount_percentage: 0, max_uses: "", expires_at: "" });
      setShowAdd(false);
      fetchPromos();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const startEdit = (promo: PromoCode) => {
    setEditingId(promo.id);
    setEditData({
      code: promo.code,
      discount_percentage: promo.discount_percentage,
      max_uses: promo.max_uses?.toString() || "",
      expires_at: promo.expires_at ? promo.expires_at.slice(0, 10) : "",
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      const { error } = await supabase.from("promo_codes").update({
        code: editData.code.toUpperCase().trim(),
        discount_percentage: editData.discount_percentage,
        max_uses: editData.max_uses ? parseInt(editData.max_uses) : null,
        expires_at: editData.expires_at || null,
      } as any).eq("id", editingId);
      if (error) throw error;
      toast({ title: "Success", description: "Promo code updated" });
      setEditingId(null);
      fetchPromos();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase.from("promo_codes").update({ is_active: !isActive } as any).eq("id", id);
      if (error) throw error;
      setPromos(promos.map(p => p.id === id ? { ...p, is_active: !isActive } : p));
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const deletePromo = async (id: string) => {
    if (!confirm("Delete this promo code?")) return;
    try {
      const { error } = await supabase.from("promo_codes").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Success", description: "Promo code deleted" });
      fetchPromos();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({ title: "Copied!", description: `${code} copied to clipboard` });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-serif">Promo Codes</h1>
        <Button variant="bronze" onClick={() => setShowAdd(!showAdd)}>
          <Plus className="w-4 h-4 mr-2" />Add Promo Code
        </Button>
      </div>

      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-card border border-border rounded-sm p-4 mb-6 space-y-4"
          >
            <h3 className="font-serif text-lg">Create New Promo Code</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Code *</label>
                <Input placeholder="e.g. SUMMER25" value={newPromo.code} onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Discount % *</label>
                <Input type="number" min={1} max={100} placeholder="e.g. 25" value={newPromo.discount_percentage || ""} onChange={(e) => setNewPromo({ ...newPromo, discount_percentage: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Max Uses (optional)</label>
                <Input type="number" min={1} placeholder="Unlimited" value={newPromo.max_uses} onChange={(e) => setNewPromo({ ...newPromo, max_uses: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Expires (optional)</label>
                <Input type="date" value={newPromo.expires_at} onChange={(e) => setNewPromo({ ...newPromo, expires_at: e.target.value })} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="bronze" onClick={addPromo}>Create</Button>
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        <AnimatePresence>
          {promos.map((promo) => (
            <motion.div
              key={promo.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-card border border-border rounded-sm p-4 space-y-3"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-mono font-bold text-primary">{promo.code}</p>
                    <button onClick={() => copyCode(promo.code)} className="text-muted-foreground hover:text-foreground">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className="text-lg font-medium">{promo.discount_percentage}% off</p>
                </div>
                <Switch checked={promo.is_active} onCheckedChange={() => toggleActive(promo.id, promo.is_active)} />
              </div>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Used: {promo.times_used}{promo.max_uses ? ` / ${promo.max_uses}` : " (unlimited)"}</p>
                {promo.expires_at && <p>Expires: {format(new Date(promo.expires_at), "MMM d, yyyy")}</p>}
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <Button variant="ghost" size="sm" onClick={() => startEdit(promo)}><Pencil className="w-4 h-4 mr-1" />Edit</Button>
                <Button variant="ghost" size="sm" onClick={() => deletePromo(promo.id)} className="text-destructive hover:text-destructive/80"><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {promos.length === 0 && (
          <div className="p-8 text-center text-muted-foreground bg-card border border-border rounded-sm">
            No promo codes yet. Create one to get started.
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-card border border-border rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Code</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Discount</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Usage</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Expires</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Active</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {promos.map((promo) => (
                  <motion.tr
                    key={promo.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="border-b border-border/50 hover:bg-muted/20 transition-colors"
                  >
                    {editingId === promo.id ? (
                      <>
                        <td className="px-4 py-3"><Input value={editData.code} onChange={(e) => setEditData({ ...editData, code: e.target.value })} /></td>
                        <td className="px-4 py-3"><Input type="number" min={1} max={100} value={editData.discount_percentage || ""} onChange={(e) => setEditData({ ...editData, discount_percentage: parseFloat(e.target.value) || 0 })} className="w-20" /></td>
                        <td className="px-4 py-3"><Input type="number" min={1} placeholder="∞" value={editData.max_uses} onChange={(e) => setEditData({ ...editData, max_uses: e.target.value })} className="w-20" /></td>
                        <td className="px-4 py-3"><Input type="date" value={editData.expires_at} onChange={(e) => setEditData({ ...editData, expires_at: e.target.value })} /></td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={saveEdit}><Check className="w-4 h-4 text-green-500" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-primary">{promo.code}</span>
                            <button onClick={() => copyCode(promo.code)} className="text-muted-foreground hover:text-foreground"><Copy className="w-3.5 h-3.5" /></button>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium">{promo.discount_percentage}%</td>
                        <td className="px-4 py-3 text-muted-foreground">{promo.times_used}{promo.max_uses ? ` / ${promo.max_uses}` : " / ∞"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{promo.expires_at ? format(new Date(promo.expires_at), "MMM d, yyyy") : "Never"}</td>
                        <td className="px-4 py-3"><Switch checked={promo.is_active} onCheckedChange={() => toggleActive(promo.id, promo.is_active)} /></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={() => startEdit(promo)}><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => deletePromo(promo.id)} className="text-destructive hover:text-destructive/80"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </td>
                      </>
                    )}
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
          {promos.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              No promo codes yet. Create one to start offering discounts.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPromoCodes;
